import React, { useCallback } from 'react';
import { BaseEntityField, BaseInfoCompProps, ModeFormInputSection, ModeInfoCompTextInputField, useModeFormStyle as modeFormStyle, usePropValue }
    from '@moderepo/mode-app-components';
import produce, { Draft } from 'immer';
import clsx from 'clsx';
import { FormControl, FormHelperText } from '@material-ui/core';
import { MetricsMetadata, MetricMetadata, TSDBDashboardWidgetDataSourceInputErrors } from '../../containers';
import { dashboardWidgetCompStyle } from './dashboardCompUtils';


/**
 * The value types of each metadata prop. We need to create input fields for these and we need to use the right input type
 */
const metadataPropType: {
    [metadataPropName: string]: 'text' | 'number'
} = {
    displayName               : 'text',
    unit                      : 'text',
    lowValueWarningThreshold  : 'number',
    lowValueCriticalThreshold : 'number',
    highValueWarningThreshold : 'number',
    highValueCriticalThreshold: 'number',
    decimalPlaces             : 'number',
    conversionFormula         : 'text',
    yAxisMin                  : 'number',
    yAxisMax                  : 'number',
};



/**
 * When one of a metadata input prop e.g. 'displayName', 'unit', changed, it will trigger an onChange event to the container. However, we don't want
 * the container to get this event just for 1 individual prop. We want to capture this event, combine this change with the other props and then
 * dispatch onChange event for the entire metadata object.
 *
 * Let say the current metricsMetadata is
 *      {
 *          temperature: {displayName: 'Temperature'},
 *          pressure: {}
 *      }
 * And the user entered the value 'Celsius' for the Temperature metric's unit. This function will update the metricsMetadata and the new
 * metricsMetadata will become
 *      {
 *          temperature: {displayName: 'Temperature', unit: 'Celsius'},
 *          pressure: {}
 *      }
 *
 *
 * @param metricName - The seriesId or valueName, or tagName
 * @param metadataPropName - The name of the metadata prop that was updated e.g. 'displayName', 'unit', etc...
 * @param value - The updated input value
 */
const mergeMetricsMetadataInputs = (
    metricName: string,
    metadataPropName: keyof MetricMetadata,
    value: string,
    metricsMetadataInput: MetricsMetadata | undefined,
): MetricsMetadata => {
    const updatedMetadataInputs = produce(metricsMetadataInput, (draft: Draft<typeof metricsMetadataInput>) => {

        if (!draft) {
            // If this is the first time the agent entered a displayName/unit value for any of the metric then 'draft' will be undefined.
            // We just create a new object and initialize it with the input value.
            return {
                [metricName]: {
                    [metadataPropName]: value,
                },
            };
        }
        if (draft && !draft[metricName]) {
            // If the metricName DOES NOT exist yet in metricsMetadataInput then add a new object inside metricsMetadataInput and use metricName
            // as key and initialize it with the provided inputName and value.
            draft[metricName] = {
                [metadataPropName]: value,
            };
        } else if (draft && draft[metricName]) {
            // If the metricName being updated already existed in the metricsMetadataInput then just update it's metadataPropName with
            // the new value.
            draft[metricName] = {
                ...draft[metricName],
                [metadataPropName]: value,
            };
        }
        return draft;
    });

    return updatedMetadataInputs || {};
};



/**
 * Create 1 input field for a metadata prop
 */
const createMetadataInputField = (
    compProps: BaseInfoCompProps,
    inputProps: BaseEntityField<MetricMetadata>,
    inputType: 'number' | 'text',
    className: string,
    metricName: string,                     // 'temperature', 'pressure', etc...
    inputName: keyof MetricMetadata,        // 'displayName', 'unit', 'yAxisMin', etc...
    inputLabel: string,                     // 'Display Name', 'Unit', 'Y Axis Min', etc...
    inputDescription: string | undefined,
    currentInputValue: string | number | undefined,
    onChangeHandler: (metricName: string, metadataPropName: keyof MetricMetadata, value: string)=> void,
) => {
    return (
        <ModeInfoCompTextInputField
            key={`${metricName}-${inputName}-${inputLabel}`}
            className={className}
            inputType={inputType}
            compProps={compProps}
            inputProps={{
                label          : inputLabel,
                editable       : inputProps.editable,
                variant        : inputProps.variant,
                editingModeOnly: inputProps.editingModeOnly,
                description    : inputDescription,
            }}
            fieldName={inputName}
            inputValue={currentInputValue}
            onInputChange={(name, value) => {
                onChangeHandler(metricName, inputName, value !== undefined ? value.toString() : '');
            }}
        />
    );
};



/**
 * Create groups of input fields, one group for each measurement name. Each measurement will have a group of metadata input fields e.g.
 * 'Display Name', 'Unit', 'Min Value', 'Max Value', etc....
 * We don't need to create input fields for create ALL props in MetricMetadata. We only need to create input fields for the list
 * of 'editableMetadataProps'
 */
const createMetadataInputFields = (
    compProps: BaseInfoCompProps,
    inputProps: BaseEntityField<MetricMetadata>,
    editableMetadataProps: readonly (keyof MetricMetadata)[],
    currentInputValue: MetricsMetadata | undefined,
    onChangeHandler: (updatedMetricsMetadataInputs: MetricsMetadata)=> void,
    inputFieldLabels: {
        readonly displayName?: string | undefined;
        readonly unit?: string | undefined;
        readonly yAxisMin?: string | undefined;
        readonly yAxisMax?: string | undefined;
        readonly lowValueWarningThreshold?: string | undefined;
        readonly lowValueCriticalThreshold?: string | undefined;
        readonly highValueWarningThreshold?: string | undefined;
        readonly highValueCriticalThreshold?: string | undefined;
        readonly decimalPlaces?: string | undefined;
        readonly conversionFormula?: string | undefined;

    },
    inputFieldDescriptions: {
        readonly displayName?: string | undefined;
        readonly unit?: string | undefined;
        readonly yAxisMin?: string | undefined;
        readonly yAxisMax?: string | undefined;
        readonly lowValueWarningThreshold?: string | undefined;
        readonly lowValueCriticalThreshold?: string | undefined;
        readonly highValueWarningThreshold?: string | undefined;
        readonly highValueCriticalThreshold?: string | undefined;
        readonly decimalPlaces?: string | undefined;
        readonly conversionFormula?: string | undefined;
    } | undefined,
): readonly JSX.Element [] | undefined => {

    const formClasses = modeFormStyle();
    const dashboardWidgetCompClasses = dashboardWidgetCompStyle();

    /**
     * @param updatedMetricName
     * @param updatedMetricProp
     * @param newValue
     */
    const onMetadataPropInputChange = (updatedMetricName: string, updatedMetricProp: keyof MetricMetadata, newValue: string) => {
        const updatedMetricsMetadataInputs = mergeMetricsMetadataInputs(updatedMetricName, updatedMetricProp, newValue, currentInputValue);
        return onChangeHandler(updatedMetricsMetadataInputs);
    };


    if (inputProps.value && !inputProps.hidden) {

        // props.fields.metricsMetadata.error is a string therefore we need to parse it back into an object
        const metricMetadataErrors: TSDBDashboardWidgetDataSourceInputErrors['metricsMetadata'] = ((error) => {
            // try to parse the props.fields.metricsMetadata.error into
            if (error) {
                try {
                    return JSON.parse(error);
                } catch (e) {
                    return {};
                }
            }
            return {};
        })(inputProps.error);

        return Object.keys(inputProps.value).map((metricName) => {
            const currentInputValues = currentInputValue?.[metricName];
            return (
                <ModeFormInputSection
                    className={clsx(dashboardWidgetCompClasses.metadataInputSection, metricMetadataErrors?.[metricName] && 'error')}
                    key={metricName}
                    collapsible
                    collapsed
                    title={metricName}
                    icon="tune"
                >
                    <>
                        <div className={clsx(formClasses.formInputGroup, 'small')}>
                            {editableMetadataProps.map((metadataPropName) => {
                                return (
                                    createMetadataInputField(
                                        compProps,
                                        inputProps,
                                        metadataPropType[metadataPropName],
                                        `${formClasses.formInputField}`,
                                        metricName,
                                        metadataPropName,
                                        inputFieldLabels[metadataPropName] || metadataPropName,
                                        inputFieldDescriptions?.[metadataPropName],
                                        currentInputValues?.[metadataPropName],
                                        onMetadataPropInputChange,
                                    )
                                );
                            })}
                        </div>
                        {metricMetadataErrors?.[metricName] && (
                            <FormControl error={Boolean(metricMetadataErrors?.[metricName])}>
                                <FormHelperText>
                                    {metricMetadataErrors?.[metricName]}
                                </FormHelperText>
                            </FormControl>
                        )}
                    </>
                </ModeFormInputSection>
            );
        });
    }

    return undefined;
};



export interface MetricsMetadataInputsCompProps {

    readonly fields: {
        readonly metricsMetadata: BaseEntityField<MetricsMetadata>;
    }

    // The localized texts needed for for the metadata input fields
    readonly texts: {
        readonly labels: {
            readonly displayName?: string | undefined;
            readonly unit?: string | undefined;
            readonly yAxisMin?: string | undefined;
            readonly yAxisMax?: string | undefined;
            readonly lowValueWarningThreshold?: string | undefined;
            readonly lowValueCriticalThreshold?: string | undefined;
            readonly highValueWarningThreshold?: string | undefined;
            readonly highValueCriticalThreshold?: string | undefined;
            readonly decimalPlaces?: string | undefined;
            readonly conversionFormula?: string | undefined;

        };
        readonly descriptions?: {
            readonly displayName?: string | undefined;
            readonly unit?: string | undefined;
            readonly yAxisMin?: string | undefined;
            readonly yAxisMax?: string | undefined;
            readonly lowValueWarningThreshold?: string | undefined;
            readonly lowValueCriticalThreshold?: string | undefined;
            readonly highValueWarningThreshold?: string | undefined;
            readonly highValueCriticalThreshold?: string | undefined;
            readonly decimalPlaces?: string | undefined;
            readonly conversionFormula?: string | undefined;
        } | undefined;
    }

    // The set of metadata that can be configured. Not all metadata are required for all widget. Some widget only required 'name', and 'unit'.
    // Some widget required threshold settings, etc.... The container needs to specify which metadata it wants to enable.
    readonly editableMetadata: readonly (keyof MetricMetadata)[];

    readonly isEditing?: boolean | undefined;
    readonly onFieldClicked?: (fieldName: string)=> void;

    // Handler for when the entire data source input is updated.
    readonly onInputChanged?: (metricsMetadataInputs: MetricsMetadata)=> void | undefined;
}



/**
 * This component is used for the metadata input fields for multiple metrics.
 */
export const MetricsMetadataInputsComp: React.FC<MetricsMetadataInputsCompProps> = (
    props: MetricsMetadataInputsCompProps,
) => {
    const [metricsMetadataInput, setMetricsMetadataInput] = usePropValue<MetricsMetadata | undefined>(props.fields.metricsMetadata.value);

    const onMetricsMetadataInputChange = useCallback((updatedMetadataInputs: MetricsMetadata) => {
        if (metricsMetadataInput !== updatedMetadataInputs) {
            setMetricsMetadataInput(updatedMetadataInputs);
            if (props.onInputChanged) {
                props.onInputChanged(updatedMetadataInputs);
            }
        }
    }, [metricsMetadataInput, props, setMetricsMetadataInput]);


    return (
        <>
            {/* Call helper function to create the input fields for the metricsMetadata */}
            {createMetadataInputFields(
                // Don't use the default onInputChange handler from props.onInputChange. We will capture the in put change event in the
                // onMetadataInputChange, merge all the metadata input and then dispatch props.onInputChange
                {
                    ...props,
                    onFormInputChange: undefined,
                },
                props.fields.metricsMetadata,
                props.editableMetadata,
                metricsMetadataInput,
                onMetricsMetadataInputChange,
                props.texts.labels,
                props.texts.descriptions,
            )}
        </>
    );
};
